import {
  ALPHABET,
  ALPHABET_SIZE,
  foldDiacritics,
  letterIndex,
  mod,
} from "@/lib/tool-kit/ciphers/alphabet";

/**
 * The Enigma I — the three-rotor machine of the German army and air force — with the
 * five rotors it was issued with and reflectors B and C.
 *
 * The wirings and turnover letters are the published ones (Wikipedia, "Enigma rotor
 * details"): rotors I–III from 1930, IV and V from December 1938, reflector B as the
 * standard reflector from 1937, and C, used only briefly during the war. The entry wheel
 * of the Enigma I is wired straight through (A to A), so it drops out of the arithmetic.
 *
 * A key press does two things, in this order:
 *
 * 1. **The rotors step.** The right rotor always moves on one letter. When the right
 *    rotor leaves its turnover letter, it carries the middle rotor one step. When the
 *    middle rotor is itself at its turnover letter, it steps again on the next key
 *    press and carries the left rotor with it — the "double step", which is why the
 *    middle rotor sometimes moves on two key presses in a row (ADU → ADV → AEW → BFX).
 * 2. **The current flows.** Plugboard, right, middle and left rotor, reflector, back
 *    through the left, middle and right rotor, plugboard, lamp.
 *
 * The turnover notch sits on the alphabet ring, so it is tied to the letter in the
 * window, not to the wiring: the ring setting moves the wiring against the letters and
 * leaves the stepping alone.
 */

export type RotorName = "I" | "II" | "III" | "IV" | "V";
export type ReflectorName = "B" | "C";

export interface RotorSpec {
  wiring: string;
  /** The window letter the rotor is on when it carries its left-hand neighbour. */
  turnover: string;
}

export const ROTORS: Readonly<Record<RotorName, RotorSpec>> = {
  I: { wiring: "EKMFLGDQVZNTOWYHXUSPAIBRCJ", turnover: "Q" },
  II: { wiring: "AJDKSIRUXBLHWTMCQGZNPYFVOE", turnover: "E" },
  III: { wiring: "BDFHJLCPRTXVZNYEIWGAKMUSQO", turnover: "V" },
  IV: { wiring: "ESOVPZJAYQUIRHXLNFTGKDCMWB", turnover: "J" },
  V: { wiring: "VZBRGITYUPSDNHLXAWMJQOFECK", turnover: "Z" },
};

export const ROTOR_NAMES: readonly RotorName[] = ["I", "II", "III", "IV", "V"];

export const REFLECTORS: Readonly<Record<ReflectorName, string>> = {
  B: "YRUHQSLDPXNGOKMIEBFZCWVJAT",
  C: "FVPJIAOYEDRZXWGCTKUQSBNMHL",
};

export const REFLECTOR_NAMES: readonly ReflectorName[] = ["B", "C"];

/** The Enigma I was issued with ten plugboard cables. */
export const MAX_PLUG_PAIRS = 10;

/** Left, middle, right — the order the rotors sit in, as a key sheet lists them. */
export type Triple<T> = readonly [T, T, T];

export interface EnigmaSettings {
  reflector: ReflectorName;
  rotors: Triple<RotorName>;
  /** Ring settings, 0-based: A (01) is 0. */
  rings: Triple<number>;
  /** The letters in the windows before the first key press, 0-based. */
  positions: Triple<number>;
  /** Plugboard pairs as capital letters, e.g. ["AV", "BS"]. */
  plugs: readonly string[];
}

/* -------------------------------------------------------------- wiring maps */

function wiringIndexes(wiring: string): number[] {
  return [...wiring].map((letter) => letterIndex(letter));
}

function inverse(map: readonly number[]): number[] {
  const result: number[] = Array.from({ length: ALPHABET_SIZE }, () => 0);
  for (const [index, value] of map.entries()) {
    result[value] = index;
  }
  return result;
}

const FORWARD: Readonly<Record<RotorName, readonly number[]>> = {
  I: wiringIndexes(ROTORS.I.wiring),
  II: wiringIndexes(ROTORS.II.wiring),
  III: wiringIndexes(ROTORS.III.wiring),
  IV: wiringIndexes(ROTORS.IV.wiring),
  V: wiringIndexes(ROTORS.V.wiring),
};

const BACKWARD: Readonly<Record<RotorName, readonly number[]>> = {
  I: inverse(FORWARD.I),
  II: inverse(FORWARD.II),
  III: inverse(FORWARD.III),
  IV: inverse(FORWARD.IV),
  V: inverse(FORWARD.V),
};

const REFLECTOR_MAPS: Readonly<Record<ReflectorName, readonly number[]>> = {
  B: wiringIndexes(REFLECTORS.B),
  C: wiringIndexes(REFLECTORS.C),
};

/** Current entering a rotor's contact `input` comes out at the returned contact. */
function throughRotor(
  map: readonly number[],
  input: number,
  position: number,
  ring: number
): number {
  const shift = position - ring;
  return mod(map[mod(input + shift, ALPHABET_SIZE)] - shift, ALPHABET_SIZE);
}

/* ------------------------------------------------------------------ plugboard */

export type PlugboardProblem =
  | { kind: "odd"; letter: string }
  | { kind: "same"; letter: string }
  | { kind: "reused"; letter: string }
  | { kind: "tooMany"; count: number };

export interface PlugboardReading {
  pairs: string[];
  problems: PlugboardProblem[];
}

const PAIR_SEPARATORS = /[\s,;/]+/;

/**
 * "AV BS CG" (spaces, commas or semicolons between pairs) → ["AV", "BS", "CG"].
 *
 * A pair with one letter, a letter plugged to itself, a letter in two pairs and an
 * eleventh cable are reported, and the pairs that are fine are still used — so a
 * half-typed board keeps working while the reader finishes it.
 */
export function parsePlugboard(text: string): PlugboardReading {
  const pairs: string[] = [];
  const problems: PlugboardProblem[] = [];
  const used = new Set<string>();
  const tokens = foldDiacritics(text).toUpperCase().split(PAIR_SEPARATORS);
  for (const token of tokens) {
    const letters = [...token].filter((char) => letterIndex(char) >= 0);
    if (letters.length === 0) {
      continue;
    }
    if (letters.length !== 2) {
      problems.push({ kind: "odd", letter: letters.join("") });
      continue;
    }
    const [a, b] = letters;
    if (a === b) {
      problems.push({ kind: "same", letter: a });
      continue;
    }
    const reused = [a, b].find((letter) => used.has(letter));
    if (reused) {
      problems.push({ kind: "reused", letter: reused });
      continue;
    }
    if (pairs.length >= MAX_PLUG_PAIRS) {
      problems.push({ kind: "tooMany", count: pairs.length + 1 });
      continue;
    }
    used.add(a);
    used.add(b);
    pairs.push(`${a}${b}`);
  }
  return { pairs, problems };
}

function plugboardMap(pairs: readonly string[]): number[] {
  const map = Array.from({ length: ALPHABET_SIZE }, (_, index) => index);
  for (const pair of pairs) {
    const a = letterIndex(pair.charAt(0));
    const b = letterIndex(pair.charAt(1));
    if (a >= 0 && b >= 0) {
      map[a] = b;
      map[b] = a;
    }
  }
  return map;
}

/* -------------------------------------------------------------------- stepping */

function atTurnover(rotor: RotorName, position: number): boolean {
  return (
    ALPHABET.charAt(mod(position, ALPHABET_SIZE)) === ROTORS[rotor].turnover
  );
}

/** The window letters after one key press, including the double step. */
export function stepRotors(
  rotors: Triple<RotorName>,
  positions: Triple<number>
): Triple<number> {
  const [left, middle, right] = positions;
  let nextLeft = left;
  let nextMiddle = middle;
  if (atTurnover(rotors[1], middle)) {
    nextMiddle = middle + 1;
    nextLeft = left + 1;
  } else if (atTurnover(rotors[2], right)) {
    nextMiddle = middle + 1;
  }
  return [
    mod(nextLeft, ALPHABET_SIZE),
    mod(nextMiddle, ALPHABET_SIZE),
    mod(right + 1, ALPHABET_SIZE),
  ];
}

/** Window positions as letters: [0, 3, 20] → "ADU". */
export function windowLetters(positions: Triple<number>): string {
  return positions.map((position) => ALPHABET.charAt(position)).join("");
}

/* ----------------------------------------------------------------- the machine */

/**
 * Where the current goes for one key press, letter by letter: the key, after the
 * plugboard, after the right, middle and left rotor, after the reflector, after the
 * left, middle and right rotor on the way back, and the lamp after the plugboard.
 */
export interface SignalPath {
  /** Window letters while the key is held — after the rotors stepped. */
  window: string;
  /** Ten letters, key to lamp. */
  path: string[];
}

export interface EnigmaPress {
  input: string;
  output: string;
  window: string;
}

export interface EnigmaRun {
  /** One entry per letter enciphered. */
  presses: EnigmaPress[];
  /** The enciphered letters alone, capitals. */
  letters: string;
  /** The window letters after the last key press. */
  finalWindow: string;
  /** The path of the last key press, or null when no letter was typed. */
  lastPath: SignalPath | null;
}

interface Machine {
  settings: EnigmaSettings;
  plugboard: number[];
  positions: Triple<number>;
}

function createMachine(settings: EnigmaSettings): Machine {
  return {
    settings,
    plugboard: plugboardMap(settings.plugs),
    positions: settings.positions,
  };
}

function press(machine: Machine, letter: number): SignalPath {
  const { rotors, rings, reflector } = machine.settings;
  machine.positions = stepRotors(rotors, machine.positions);
  const [left, middle, right] = machine.positions;
  const stages: number[] = [letter];
  let signal = machine.plugboard[letter];
  stages.push(signal);
  signal = throughRotor(FORWARD[rotors[2]], signal, right, rings[2]);
  stages.push(signal);
  signal = throughRotor(FORWARD[rotors[1]], signal, middle, rings[1]);
  stages.push(signal);
  signal = throughRotor(FORWARD[rotors[0]], signal, left, rings[0]);
  stages.push(signal);
  signal = REFLECTOR_MAPS[reflector][signal];
  stages.push(signal);
  signal = throughRotor(BACKWARD[rotors[0]], signal, left, rings[0]);
  stages.push(signal);
  signal = throughRotor(BACKWARD[rotors[1]], signal, middle, rings[1]);
  stages.push(signal);
  signal = throughRotor(BACKWARD[rotors[2]], signal, right, rings[2]);
  stages.push(signal);
  stages.push(machine.plugboard[signal]);
  return {
    window: windowLetters(machine.positions),
    path: stages.map((stage) => ALPHABET.charAt(stage)),
  };
}

/**
 * Type every A–Z letter of `text` into a machine set up as `settings`. Accented letters
 * are typed as their base letter; anything else has no key and is not typed.
 */
export function runEnigma(text: string, settings: EnigmaSettings): EnigmaRun {
  const machine = createMachine(settings);
  const presses: EnigmaPress[] = [];
  let lastPath: SignalPath | null = null;
  let letters = "";
  for (const char of foldDiacritics(text)) {
    const index = letterIndex(char);
    if (index < 0) {
      continue;
    }
    lastPath = press(machine, index);
    const output = lastPath.path.at(-1) ?? "";
    presses.push({
      input: ALPHABET.charAt(index),
      output,
      window: lastPath.window,
    });
    letters += output;
  }
  return {
    presses,
    letters,
    finalWindow: windowLetters(machine.positions),
    lastPath,
  };
}

/** Just the enciphered letters, capitals, nothing else. */
export function enigma(text: string, settings: EnigmaSettings): string {
  return runEnigma(text, settings).letters;
}

/**
 * The enciphered letters put back where the input's letters were, so spaces and
 * punctuation stay in place. Letters keep the input's case. A real operator had no keys
 * for any of this — spaces were left out or sent as X — so this is a reading aid.
 */
export function enigmaInPlace(text: string, run: EnigmaRun): string {
  let result = "";
  let next = 0;
  for (const char of foldDiacritics(text)) {
    const index = letterIndex(char);
    if (index < 0) {
      result += char;
      continue;
    }
    const output = run.presses[next]?.output ?? "";
    next += 1;
    result += char === char.toLowerCase() ? output.toLowerCase() : output;
  }
  return result;
}

/** True when no rotor appears twice — the machine has one of each. */
export function rotorsDistinct(rotors: Triple<RotorName>): boolean {
  return new Set(rotors).size === rotors.length;
}
